/**
 * A prescrição **enquanto está sendo digitada**, que não é a mesma coisa que a
 * prescrição que o servidor aceita.
 *
 * Existe por causa de um defeito real: os campos obrigatórios voltavam a `1`
 * quando apagados. O `onChange` fazia `v ?? 1`, então limpar "4" para digitar
 * "12" repunha "1" no instante em que o campo esvaziava — e a pessoa via a tela
 * recusar a edição dela, sem mensagem nenhuma. Parecia defeito porque era.
 *
 * A correção honesta não é enfeitar o campo: é aceitar que **vazio é um estado
 * válido de edição** e inválido de gravação. Estes tipos permitem o vazio; a
 * gravação exige o preenchido, e `missingFields` diz o que falta.
 *
 * Os tipos de `api.ts` continuam descrevendo fielmente o que a API aceita —
 * afrouxá-los ali faria o contrato mentir para poder acomodar um campo em
 * branco na tela.
 */

import type {
  CardioPrescription,
  FunctionalPrescription,
  MobilityPrescription,
  Prescription,
  StrengthPrescription,
  SwimSet,
  SwimmingPrescription,
} from './api';

/** Os campos `K` de `T` passam a aceitar `null`. */
type Editando<T, K extends keyof T> = Omit<T, K> & { [P in K]: T[P] | null };

export type DraftStrength = Editando<StrengthPrescription, 'sets' | 'reps_min' | 'reps_max'>;
export type DraftFunctional = Editando<FunctionalPrescription, 'rounds'>;
export type DraftSwimSet = Editando<SwimSet, 'repetitions' | 'distance_m'>;
export type DraftSwimming = Omit<SwimmingPrescription, 'sets'> & { sets?: DraftSwimSet[] };

export type DraftPrescription =
  DraftStrength | CardioPrescription | DraftFunctional | MobilityPrescription | DraftSwimming;

/**
 * Os campos obrigatórios ainda vazios, como chaves de tradução.
 *
 * Devolve a lista inteira, e não o primeiro: preencher um campo por vez, cada
 * um revelado depois de tentar salvar, é a mesma tentativa e erro que a
 * curadoria evita ao listar todas as pendências de uma vez.
 */
export function missingFields(prescription: DraftPrescription): string[] {
  const faltando: string[] = [];

  if (prescription.type === 'strength') {
    if (prescription.sets === null) faltando.push('sets');
    if (prescription.reps_min === null) faltando.push('repsMin');
    if (prescription.reps_max === null) faltando.push('repsMax');
  }

  if (prescription.type === 'functional' && prescription.rounds === null) {
    faltando.push('rounds');
  }

  if (prescription.type === 'swimming') {
    const series = prescription.sets ?? [];
    if (series.some((serie) => serie.repetitions === null)) faltando.push('repetitions');
    if (series.some((serie) => serie.distance_m === null)) faltando.push('distanceM');
  }

  return faltando;
}

/**
 * Converte para o formato que a API aceita — ou `null` se ainda falta algo.
 *
 * O `as` no fim é seguro **porque** `missingFields` acabou de confirmar que
 * nenhum obrigatório está vazio. Sem essa checagem antes, seria uma mentira
 * para o compilador.
 */
export function toPrescription(prescription: DraftPrescription): Prescription | null {
  if (missingFields(prescription).length > 0) return null;
  return prescription as Prescription;
}
